/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { FeatureCollection } from 'geojson';
import {
  createEmpty,
  extend as extendExtent,
  isEmpty as isEmptyExtent,
} from 'ol/extent';
import GeoJSON from 'ol/format/GeoJSON';
import React, { useRef } from 'react';
import { defaultBbox as defaultBboxCore } from '@opengeoweb/core';
import type { MapPreset } from '@opengeoweb/store';
import { LayerType, generateMapId } from '@opengeoweb/webmap';
import {
  OpenLayersFeatureLayer,
  OpenLayersLayer,
  OpenLayersMapView,
  defaultLayers,
  genericOpenLayersFeatureStyle,
  OpenLayersZoomControl,
  MapControls,
  viewUtils,
} from '@opengeoweb/webmap-react';
import type { OpenLayersLayerProps } from '@opengeoweb/webmap-react';

export const EXTENT_PADDING = 50;
const DEFAULT_SIZE = { height: '200px', width: '100%' };
function getGeojsonExtent(
  featureCollection: FeatureCollection,
  viewSrs: string,
  dataSrs = 'EPSG:4326', // change if GeoJSON is not lon/lat
): number[] {
  const format = new GeoJSON();
  const features = format.readFeatures(featureCollection, {
    dataProjection: dataSrs,
    featureProjection: viewSrs,
  });

  const extent = createEmpty();
  for (const feat of features) {
    const geo = feat.getGeometry();
    if (geo) {
      extendExtent(extent, geo.getExtent());
    }
  }
  return extent;
}

function getContainerSize(
  el: HTMLDivElement | null,
): [number, number] | undefined {
  const rect = el?.getBoundingClientRect();
  return rect && rect.width > 0 && rect.height > 0
    ? [Math.round(rect.width), Math.round(rect.height)]
    : undefined;
}

// Padding scales with container size (10%) so small containers keep most of their area usable,
// while large containers cap at EXTENT_PADDING.
const getSafeExtentPadding = (size: [number, number] | undefined): number[] => {
  if (!size) {
    return [EXTENT_PADDING, EXTENT_PADDING, EXTENT_PADDING, EXTENT_PADDING];
  }

  const [width, height] = size;
  const padX = Math.min(EXTENT_PADDING, Math.floor(width * 0.1));
  const padY = Math.min(EXTENT_PADDING, Math.floor(height * 0.1));
  return [padY, padX, padY, padX];
};

interface WarningsMapViewProps {
  geojsonFeature?: FeatureCollection | undefined;
  defaultMapPreset?: MapPreset;
  isSnapshot?: boolean;
  size?: { height: string; width: string };
  showMapMousePosition?: boolean;
  showZoomControls?: boolean;
}
export const WarningsMapView: React.FC<WarningsMapViewProps> = ({
  geojsonFeature,
  defaultMapPreset,
  isSnapshot = false,
  size = DEFAULT_SIZE,
  showMapMousePosition = true,
  showZoomControls = true,
}: WarningsMapViewProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const defaultMapPresetBbox = geojsonFeature?.features.length
    ? undefined
    : defaultBboxCore;
  const defaultBbox = defaultMapPreset?.proj || defaultMapPresetBbox;

  const defaultBaseLayer =
    defaultMapPreset?.layers?.find(
      (layer) => layer.layerType === LayerType.baseLayer,
    ) || defaultLayers.baseLayerGrey;
  const defaultOverLayer =
    defaultMapPreset?.layers?.find(
      (layer) => layer.layerType === LayerType.overLayer,
    ) || defaultLayers.overLayer;

  const mapId = React.useRef<string>(
    `WarningsMapView_${generateMapId()}`,
  ).current;

  const warningLayer: OpenLayersLayerProps = {
    id: `${mapId}geojsonfeature`,
    geojson: geojsonFeature,
    layerType: LayerType.featureLayer,
  };

  const view = React.useMemo(
    () => viewUtils.makeView(defaultBbox?.srs),
    [defaultBbox?.srs],
  );

  const viewSrs = view.getProjection().getCode();
  const geojsonExtent = React.useMemo(
    () =>
      geojsonFeature?.features?.length
        ? getGeojsonExtent(geojsonFeature, viewSrs, 'EPSG:4326')
        : undefined,
    [geojsonFeature, viewSrs],
  );

  const fitViewToExtent = React.useCallback((): void => {
    const pixelSize = getContainerSize(containerRef.current);
    const padding = getSafeExtentPadding(pixelSize);
    const bbox = defaultBbox?.bbox;

    if (bbox && !geojsonFeature?.features.length) {
      viewUtils.setViewFromExtent(view, bbox);
      return;
    }

    if (geojsonExtent && !isEmptyExtent(geojsonExtent)) {
      view.fit(geojsonExtent, {
        size: pixelSize,
        padding,
        maxZoom: 12,
        duration: 0,
      });

      return;
    }

    // fallback
    viewUtils.setViewFromFeature(view, geojsonFeature);
  }, [defaultBbox, geojsonFeature, geojsonExtent, view]);

  React.useEffect(() => {
    fitViewToExtent();
  }, [fitViewToExtent]);

  return (
    <div ref={containerRef} style={size}>
      <OpenLayersMapView
        holdShiftToScroll={true}
        mapId={mapId}
        showMapMousePosition={showMapMousePosition}
        view={view}
      >
        {!!showZoomControls && (
          <MapControls style={{ top: 0 }}>
            <OpenLayersZoomControl homeExtent={view.calculateExtent()} />
          </MapControls>
        )}
        {!isSnapshot && (
          <OpenLayersLayer {...defaultBaseLayer} id={`${mapId}_baseLayer`} />
        )}
        {!!warningLayer.geojson && (
          <OpenLayersFeatureLayer
            featureCollection={warningLayer.geojson}
            style={genericOpenLayersFeatureStyle}
            zIndex={1000}
          />
        )}
        {!isSnapshot && (
          <OpenLayersLayer {...defaultOverLayer} id={`${mapId}_overlayer`} />
        )}
      </OpenLayersMapView>
    </div>
  );
};
